import { useDetailsDrawer } from '@src/components/DetailsDrawer/useDetailsDrawer';
import {
  BlotterTable,
  BlotterTableExtrasMenu,
  BlotterTableFilters,
  Button,
  ButtonVariants,
  CollapseAllButton,
  DEFAULT_BLOTTER_SELECTION_MULTI_PARAMS,
  Divider,
  ExpandAllButton,
  ExportMenuItems,
  FormControlSizes,
  IconName,
  MixpanelEvent,
  columnTypes,
  useAccordionFilterBuilder,
  useBlotterTable,
  useBlotterTableExtrasMenu,
  useCallbackRef,
  useDateRangeFilter,
  useMixpanel,
  usePersistedBlotterTable,
  type BlotterTableRow,
  type CareOrder,
} from '@talos/kyoko';
import type { RowDoubleClickedEvent } from 'ag-grid-enterprise';
import { useMemo, useState } from 'react';
import { useFeatureFlag } from '../../../../hooks';
import { useQueryBlotterDataObservable } from '../../../../hooks/useQueryBlotterDataObservable';
import { useGetCareOrdersQuery } from '../../../../providers/AppStateProvider/streamingDataSlice';
import { CareOrderBlotterHUD } from '../CareOrderBlotterHUD';
import { CareOrderNetButton } from '../CareOrderNetButton';
import { IncludeOpenCareOrdersOutsideDateRangeToggle } from '../IncludeOpenCareOrdersOutsideDateRangeToggle';
import { useNetCareOrdersMenuItem } from '../MenuItems/useNetCareOrdersMenuItem';
import { careOrderTimestampFields } from '../useCareOrderFilter';
import { useCareOrderManagementColumns } from './useCareOrderManagementColumns';
import { useCareOrderManagementFilters } from './useCareOrderManagementFilters';
import { getSelectedCareOrderRowsFromNodes, useCareOrderManagementMenu } from './useCareOrderManagementMenu';

interface CareOrderManagementBlotterProps {
  blotterID: string;
  portalId?: string;
  showHUD?: boolean;
  includeOpenCareOrdersOutsideDateRange?: boolean;
  onUpdateTabData?: (data: CareOrderManagementTabData) => void;
  onCloneTab?: () => void;
}

export interface CareOrderManagementTabData {
  includeOpenCareOrdersOutsideDateRange?: boolean;
}

export function CareOrderManagementBlotter({
  blotterID,
  portalId,
  showHUD,
  includeOpenCareOrdersOutsideDateRange = true,
  onUpdateTabData,
  onCloneTab,
}: CareOrderManagementBlotterProps) {
  const mixpanel = useMixpanel();
  const { enableCareOrderNetting } = useFeatureFlag();
  const columns = useCareOrderManagementColumns();
  const [selectedCareOrders, setSelectedCareOrders] = useState<CareOrder[]>([]);
  const persistedBlotterTable = usePersistedBlotterTable(blotterID);
  const { openDetailsDrawer } = useDetailsDrawer();

  const handleDoubleClickRow = useCallbackRef(({ data }: RowDoubleClickedEvent<CareOrder>) => {
    if (data) {
      openDetailsDrawer({ entity: data });
    }
  });

  const filterResults = useCareOrderManagementFilters({
    filterProps: {
      initialFilter: persistedBlotterTable.initialState?.filter,
      onFilterChanged: persistedBlotterTable.onFilterChanged,
    },
    includeOpenCareOrdersOutsideDateRange,
  });

  const filterBuilderAccordion = useAccordionFilterBuilder({
    accordionProps: { initialOpen: false },
    filterBuilderProps: filterResults.filterBuilderProps,
  });
  const dateRangeFilter = useDateRangeFilter(filterResults.filter, filterResults.changeFilter);
  const careOrdersQuery = useGetCareOrdersQuery({ tag: 'CareOrder management blotter' });

  const { dataObservable, errorObservable } = useQueryBlotterDataObservable({
    queryResult: careOrdersQuery,
  });

  const autoGroupColumnDef = useMemo(
    () =>
      columnTypes.group({
        type: 'group',
        title: 'Grouping',
        editable: false,
        width: 150,
        hide: false,
        sortable: true,
        suppressColumnsToolPanel: false,
        params: {
          suppressCount: true,
        },
      }),
    []
  );

  const netCareOrdersMenu = useNetCareOrdersMenuItem<CareOrder>();
  const careOrderManagementMenu = useCareOrderManagementMenu({
    filterableProperties: filterResults.filterableProperties,
    openClause: filterBuilderAccordion.openClause,
    netCareOrdersMenu,
  });

  const blotterTable = useBlotterTable<CareOrder>({
    dataObservable,
    errorObservable,
    rowID: 'OrderID',
    persistence: persistedBlotterTable,
    clientLocalFilter: filterResults.clientSideFilter,
    columns,
    gridOptions: {
      rowSelection: DEFAULT_BLOTTER_SELECTION_MULTI_PARAMS,
      treeData: false,
      rowGroupPanelShow: 'always',
      groupDisplayType: 'singleColumn',
      tooltipInteraction: true,
      autoGroupColumnDef,
      onRowDoubleClicked: handleDoubleClickRow,
      onSelectionChanged: event => {
        setSelectedCareOrders(getSelectedCareOrderRowsFromNodes(event.api.getSelectedNodes()));
      },
    },
    contextMenuOptions: {
      getCustomContextMenuItems: careOrderManagementMenu.getCustomContextMenuItems,
    },
    exportOptions: {
      name: 'CareOrderManagement',
    },
  });

  const extrasMenuPopover = useBlotterTableExtrasMenu();
  const handleCloneTab = useCallbackRef(() => {
    mixpanel.track(MixpanelEvent.CloneTab);
    onCloneTab?.();
    extrasMenuPopover.close();
  });
  const handleIncludeOpenCareOrdersOutsideDateRangeChange = useCallbackRef((value: boolean) => {
    onUpdateTabData?.({ includeOpenCareOrdersOutsideDateRange: value });
  });

  return (
    <>
      <BlotterTableFilters
        portalId={portalId}
        {...filterBuilderAccordion}
        {...dateRangeFilter}
        {...blotterTable.blotterTableFiltersProps}
        timestampFields={careOrderTimestampFields}
        quickFilterPrefix={
          enableCareOrderNetting ? (
            <CareOrderNetButton selectedCareOrders={selectedCareOrders} netCareOrdersMenu={netCareOrdersMenu} />
          ) : undefined
        }
        suffix={
          <>
            <ExpandAllButton onClick={() => blotterTable.expandAllGroups()} data-testid="expand-all-button" />
            <CollapseAllButton onClick={() => blotterTable.collapseAllGroups()} data-testid="collapse-all-button" />
            <Divider orientation="vertical" mx="spacing4" />
            <BlotterTableExtrasMenu {...extrasMenuPopover}>
              {onUpdateTabData && (
                <IncludeOpenCareOrdersOutsideDateRangeToggle
                  checked={includeOpenCareOrdersOutsideDateRange}
                  onChange={handleIncludeOpenCareOrdersOutsideDateRangeChange}
                />
              )}
              {onCloneTab && (
                <Button
                  startIcon={IconName.Duplicate}
                  variant={ButtonVariants.Default}
                  size={FormControlSizes.Small}
                  onClick={handleCloneTab}
                >
                  Clone Tab
                </Button>
              )}
              <ExportMenuItems blotterTable={blotterTable} handleClose={extrasMenuPopover.close} />
            </BlotterTableExtrasMenu>
          </>
        }
      />
      {showHUD && <CareOrderBlotterHUD blotterTable={blotterTable} getCareOrder={getCareOrderFromBlotterTableRow} />}
      <BlotterTable {...blotterTable} />
      {careOrderManagementMenu.dialogs}
    </>
  );
}

function getCareOrderFromBlotterTableRow(row: BlotterTableRow<CareOrder>) {
  return row.data;
}
